/**
 * Dialog frame shared by every form and confirmation of the back-office.
 *
 * Two shapes: a centred modal, and a side drawer that keeps the list in view
 * behind it (order detail, docs/15 section 4.3). Escape and the backdrop
 * close it; focus moves inside on open and returns to the trigger on close.
 */

import { useEffect, useId, useRef, type ReactNode } from 'react';

import { labels } from '../labels.js';
import { Icon } from './Icon.js';

interface ModalProps {
  title: ReactNode;
  description?: ReactNode;
  onClose: () => void;
  children?: ReactNode;
  footer?: ReactNode;
  variant?: 'modal' | 'drawer';
  size?: 'default' | 'wide';
  /** Content above the title in a drawer header: badges, meta. */
  aside?: ReactNode;
  /** Blocks closing while a request is running. */
  locked?: boolean;
  /** Wraps body and footer in a form, so Enter submits. */
  onSubmit?: (e: React.FormEvent) => void;
}

export function Modal({
  title,
  description,
  onClose,
  children,
  footer,
  variant = 'modal',
  size = 'default',
  aside,
  locked = false,
  onSubmit,
}: ModalProps) {
  const titleId = useId();
  const descriptionId = useId();
  const frame = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const target =
      frame.current?.querySelector<HTMLElement>('[autofocus], input, textarea, select') ??
      frame.current?.querySelector<HTMLElement>('button, [href]');
    target?.focus();
    return () => previous?.focus?.();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !locked) {
        e.stopPropagation();
        closeRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [locked]);

  const drawer = variant === 'drawer';
  const Body = onSubmit ? 'form' : 'div';

  return (
    <div
      className={`modal-backdrop ${drawer ? 'drawer-backdrop' : ''}`}
      // A dialog opened from a dropdown must not close that dropdown's parent
      // through the outside-click handler.
      onMouseDown={(e) => {
        e.stopPropagation();
        if (e.target === e.currentTarget && !locked) onClose();
      }}
    >
      <div
        ref={frame}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={drawer ? 'drawer' : `modal ${size === 'wide' ? 'modal--wide' : ''}`}
      >
        <header className={drawer ? 'drawer__header' : 'modal__header'}>
          <div className="grow">
            {aside}
            <h2 id={titleId} className="modal__title">{title}</h2>
            {description && <p id={descriptionId} className="modal__description">{description}</p>}
          </div>
          <button
            type="button"
            className="btn-icon btn-icon--quiet btn-icon--sm"
            onClick={onClose}
            disabled={locked}
            aria-label={labels.common.close}
          >
            <Icon name="close" />
          </button>
        </header>

        <Body
          onSubmit={onSubmit}
          style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}
          noValidate={onSubmit ? false : undefined}
        >
          {children !== undefined && (
            <div className={drawer ? 'drawer__body' : 'modal__body'}>{children}</div>
          )}
          {footer && <footer className={drawer ? 'drawer__footer' : 'modal__footer'}>{footer}</footer>}
        </Body>
      </div>
    </div>
  );
}
